// media-library-change-zone.component.ts
// ============================================================================
// MEDIA LIBRARY CHANGE ZONE COMPONENT
// ============================================================================
// Purpose: Displays selected media files and provides controls to change them
// Supports both single and multiple file display modes
// Handles different media types (images, videos) with appropriate previews
// ============================================================================

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgxTippyProps } from 'ngx-tippy-wrapper';
import {
  MediaLibraryCategoryEnum,
  MediaLibraryFileModel,
} from 'src/app/models/sales-funnel';
import { decimalToImageRatio, formatFileSize, UploadMetaData } from '../utils';

@Component({
  selector: 'app-media-library-change-zone',
  templateUrl: './media-library-change-zone.component.html',
  styleUrls: ['./media-library-change-zone.component.css'],
})
export class MediaLibraryChangeZoneComponent implements OnInit {
  // ============================================================================
  // PUBLIC PROPERTIES - Enums & State
  // ============================================================================
  @Input() uploadTypeMeta: UploadMetaData;
  // @Input() uploadExtensions: Record<'image' | 'video' | 'file', string[]>;
  /** Reference to category enum for template usage */
  mediaLibraryCategoryEnum = MediaLibraryCategoryEnum;

  /** Tracks whether iframe content has finished loading (for video previews) */
  public iframeLoaded: boolean = false;

  tippyImageProps: NgxTippyProps = {
    trigger: 'click',
    followCursor: false,
    allowHTML: true,
    appendTo: document.body,
    placement: 'auto',
    maxWidth: 500,
    interactive: true,
  };

  tippyVideoProps: NgxTippyProps = {
    trigger: 'click',
    followCursor: false,
    allowHTML: true,
    interactive: true,
    appendTo: document.body,
    placement: 'auto',
    maxWidth: 650,
  };

  // ============================================================================
  // INPUT PROPERTIES - Display & Behavior Configuration
  // ============================================================================

  /** Enables view-only mode (no editing) */
  @Input() view: boolean = false;

  /** Category of uploaded media for special handling */
  @Input() uploadCategory: MediaLibraryCategoryEnum;

  /** Array of currently selected files to display */
  @Input() selectedFiles: MediaLibraryFileModel[] = [];

  /** Label text for the upload field */
  @Input() uploadFieldLabel: string = 'Upload Content';

  /** Controls visibility of the field label */
  @Input() hideFieldLabel: boolean = false;

  /** Allows selection of multiple files */
  @Input() multiFiles = true;

  /** Marks the field as required */
  @Input() required = false;

  @Input() isAvatar: boolean = false;
  /** 
   * Type of media being uploaded
   * Determines button text and display logic
   */
  @Input() uploadType:
    | 'any'
    | 'content'
    | 'media'
    | 'video'
    | 'image'
    | 'audio' = 'media';

  /** Formatted ratio display text */
  @Input() ratioDisplay: string = '';

  /** Maximum allowed file size in bytes */
  @Input() maxFileSize: number;

  /** Image specifications for display and validation */
  @Input() imgSpecs: {
    width?: number;
    height?: number;
    ratio?: number;
  };

  // ============================================================================
  // OUTPUT PROPERTIES - Event Emitters
  // ============================================================================

  /** Emitted when user clicks to change/add files */
  @Output() ClickOnActiveArea: EventEmitter<any> = new EventEmitter<any>();

  /** Emitted when user clicks to remove a file */
  @Output() onRemove: EventEmitter<MediaLibraryFileModel> = new EventEmitter<MediaLibraryFileModel>();

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor() { }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    // Calculate and format ratio display text
    if ((this.imgSpecs?.width && this.imgSpecs?.height) || this.imgSpecs?.ratio) {
      this.ratioDisplay = decimalToImageRatio(this.imgSpecs);
    } else {
      this.ratioDisplay = 'Free Ratio';
    }
  }

  get formattedMaxFileSize(): string {
    return this.maxFileSize ? formatFileSize(this.maxFileSize) : '';
  }

  // ============================================================================
  // PUBLIC METHODS - User Interaction
  // ============================================================================

  /**
   * Handles click events on the change/add button
   * Emits event to parent to open media library modal
   */
  onClickOnActiveArea(): void {
    this.ClickOnActiveArea.emit();
  }

  /**
   * Handles click events on the remove button
   * Emits event to parent to remove file from selection
   */
  removeFile(file: MediaLibraryFileModel): void {
    this.onRemove.emit(file);
  }

  /**
   * Callback when iframe finishes loading (for video streams)
   * Updates loading state for UI feedback
   */
  frameLoaded(): void {
    this.iframeLoaded = true;
  }

  // ============================================================================
  // UTILITY METHODS - Ratio Calculation
  // ============================================================================



}
